// src/components/admin/AdminHeaderSidebar.jsx
import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import { signOut } from "../../../../utils/authSession";
import {
  ExitToApp,
  CloseRoundedIcon,
} from "@mui/icons-material";
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { LogOut, ChevronDown } from "lucide-react";
import useClickOutside from "../../hooks/useClickOutside";

// Reusable Sidebar Icon Component
const SidebarIcon = ({ iconPath, hoverIconPath, label, isActive, onClick }) => {
  return (
    <div className="relative">
      {isActive && (
        <div className="absolute top-0 bottom-0 w-0.5 bg-gradient-to-b from-pink-500 to-blue-500 rounded-l-md"></div>
      )}
      <div
        onClick={onClick}
        className={`flex w-full text-white cursor-pointer rounded-md transition-all group ${
          isActive ? "bg-gradient-to-r from-purple-600/20 to-transparent" : ""
        }`}
      >
        <div className="flex flex-col items-center w-full gap-1.5 p-2.5 mt-2">
          <img
            src={isActive ? hoverIconPath : iconPath}
            alt={label}
            className="transition-all w-5 h-5 group-hover:hidden"
          />
          <img
            src={hoverIconPath}
            alt={`${label} hover`}
            className="transition-all w-5 h-5 hidden group-hover:block"
          />
          <span
            className={`text-[10px] font-light text-center w-full leading-tight opacity-80 mt-1.5 ${
              isActive ? "text-purple-400" : ""
            }`}
          >
            {label}
          </span>
        </div>
      </div>
    </div>
  );
};

const AdminHeaderSidebarAndTopbar = ({
  showDashboard,
  showOrganisations,
  showRequestDemo,
  showTickets,
  showFeedback,
  showImpersonation,
  setShowDashboard,
  setShowOrganisations,
  setShowRequestDemo,
  setShowTickets,
  setShowFeedback,
  setShowAccountInfo,
  setShowImpersonation,
  showQuickActions,
  setShowQuickActions,
  showSupportInbox,
  setShowSupportInbox,
  showCandidateFeedback,
  setShowCandidateFeedback,
  DemoProfiles,
  toggleShowDemo,
  handleAddCompany,
  profileImageUrl = "/profileheaderhover.png",
  userRole = "Admin",
}) => {

  const profileDropdownRef = useRef(null);
  const navigate = useNavigate();
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);

  const handleSignOut = async () => {
    Cookies.remove("accessToken");
    Cookies.remove("impersonationToken");
    localStorage.removeItem("impersonatedUser");
    sessionStorage.removeItem('profileChecked');
    await signOut();
    navigate("/");
  };

  // Inline SVG data-URIs for the Candidate Feedback sidebar icon (distinct from the existing Feedback icon)
  const feedbackListIconSvg =
    "data:image/svg+xml;utf8," +
    encodeURIComponent(
      '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#CCCCCC" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>'
    );
  const feedbackListIconHoverSvg =
    "data:image/svg+xml;utf8," +
    encodeURIComponent(
      '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#C084FC" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>'
    );

  const sidebarItems = [
    {
      iconPath: "/homesidebar.png",
      hoverIconPath: "/homesidebarhover.png",
      label: "Dashboard",
      active: showDashboard,
      path: "/geniehireadmin/dashboard",
    },
    {
      iconPath: "/office-building-bnw.png",
      hoverIconPath: "/office-building.png",
      label: "Accounts",
      active: showOrganisations,
      path: "/geniehireadmin/organisation",
    },
    {
      iconPath: "/Demo-bnw.png",
      hoverIconPath: "/Demo.png",
      label: "Request Demo",
      active: showRequestDemo,
      path: "/geniehireadmin/requestdemo",
    },
    {
      iconPath: "/reportssidebar.png",
      hoverIconPath: "/reportssidebarhover.png",
      label: "Feedback",
      active: showFeedback,
      path: "/geniehireadmin/feedback",
    },
    {
      iconPath: "/raiseticketsidebar.png",
      hoverIconPath: "/raiseticketsidebarhover.png",
      label: "Raised Tickets",
      active: showTickets,
      path: "/geniehireadmin/tickets",
    },
    {
      iconPath: "/teamsidebar.png",
      hoverIconPath: "/teamsidebarhover.png",
      label: "Impersonate",
      active: showImpersonation,
      path: "/geniehireadmin/impersonate",
    },
    {
      iconPath: "/raiseticketsidebar.png", // Using same icons as tickets for now
      hoverIconPath: "/raiseticketsidebarhover.png",
      label: "Quick Actions",
      active: showQuickActions,
      path: "/geniehireadmin/quickactions",
    },
    {
      iconPath: feedbackListIconSvg,
      hoverIconPath: feedbackListIconHoverSvg,
      label: "Candidate Feedback",
      active: showCandidateFeedback,
      path: "/geniehireadmin/candidate-feedback",
    },
  ];

  const resetAllTabs = () => {
    setShowDashboard(false);
    setShowOrganisations(false);
    setShowRequestDemo(false);
    setShowTickets(false);
    setShowFeedback(false);
    setShowAccountInfo(false);
    if (setShowImpersonation) setShowImpersonation(false);
    if (setShowQuickActions) setShowQuickActions(false);
    if (setShowSupportInbox) setShowSupportInbox(false);
    if (setShowCandidateFeedback) setShowCandidateFeedback(false);
  };

  useClickOutside([profileDropdownRef], () => setShowProfileDropdown(false));

  const handleNavigation = (path) => {
    resetAllTabs();
    navigate(path);

    // Set correct active tab
    if (path.includes("dashboard")) setShowDashboard(true);
    else if (path.includes("organisation")) setShowOrganisations(true);
    else if (path.includes("requestdemo")) setShowRequestDemo(true);
    else if (path.includes("feedback")) setShowFeedback(true);
    else if (path.includes("tickets")) setShowTickets(true);
    else if (path.includes("impersonate") && setShowImpersonation) setShowImpersonation(true);
    else if (path.includes("quickactions") && setShowQuickActions) setShowQuickActions(true);
    else if (path.includes("support-inbox") && setShowSupportInbox) setShowSupportInbox(true);
    else if (path.includes("candidate-feedback") && setShowCandidateFeedback) setShowCandidateFeedback(true);
  };

  return (
    <>
      {/* Sidebar */}
      <div className="bg-app-background border-r border-white/10 w-20 flex flex-col items-center py-4 min-h-screen fixed left-0 top-0 bottom-0 z-50">
        <div className="flex flex-col gap-5 items-center justify-center py-3">
          <Link to="/geniehireadmin/dashboard" onClick={() => handleNavigation("/geniehireadmin/dashboard")}>
            <img src="/Homepagelogo.png" alt="Logo" className="h-8.5 w-6" />
          </Link>

          <div className="w-full px-3 space-y-3 py-5">
            {sidebarItems.map((item) => (
              <SidebarIcon
                key={item.label}
                iconPath={item.iconPath}
                hoverIconPath={item.hoverIconPath}
                label={item.label}
                isActive={item.active}
                onClick={() => handleNavigation(item.path)}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Topbar */}
      <div className="border-b border-white/10 fixed top-0 right-0 left-20 z-50 bg-gradient-to-br from-[rgba(34,52,97,0.25)] to-[rgba(11,10,31,0.05)] backdrop-blur-[1000px]">
        <div className="flex justify-between items-center px-6 py-4">
          <div className="flex items-center gap-6">
            <button
              onClick={handleAddCompany}
              className="px-4 py-2 bg-transparent text-white rounded-full text-sm hover:bg-white/10 transition-colors border border-white/20"
            >
              Add Account
            </button>
          </div>

          <div className="flex items-center space-x-6">
            {/* Notification Bell */}
            <div className="relative group">
              {DemoProfiles?.length > 0 && (
                <div className="absolute -top-1 -right-1 text-xs bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-[10px] font-bold">
                  {DemoProfiles.length}
                </div>
              )}
              <img
                src="/notificationbell.png"
                alt="Notifications"
                onClick={toggleShowDemo}
                className="w-5 h-6 cursor-pointer group-hover:hidden"
              />
              <img
                src="/notificationbellhover.png"
                alt="Notifications Hover"
                onClick={toggleShowDemo}
                className="w-5 h-6 cursor-pointer hidden group-hover:block"
              />
            </div>

            {/* Profile Dropdown */}
            <div className="relative" ref={profileDropdownRef}>
              <button
                type="button"
                onClick={() => setShowProfileDropdown((prev) => !prev)}
                className="focus:outline-none"
              >
                <img
                  className="w-7 h-7 rounded-full border border-white/30 cursor-pointer transition-all"
                  src="/profileheader.png"
                  alt="Profile"
                />
                <img
                  className="w-7 h-7 rounded-full border border-white/30 cursor-pointer transition-all hidden hover:block"
                  src="/profileheaderhover.png"
                  alt="Profile Hover"
                />
              </button>

              {/* YOUR EXACT ORIGINAL PANEL — NOTHING CHANGED */}
              {showProfileDropdown && (
                <div className="absolute top-10 right-0 z-50">
                  <div className="w-52 rounded-2xl shadow-2xl overflow-hidden border border-purple-500/20">
                    <div className="bg-gradient-to-br from-[#0A1429] to-[#0B0A1F] backdrop-blur-3xl p-5">
                      <div className="text-center mb-4">
                        <img
                          src={profileImageUrl}
                          alt="Admin"
                          className="w-16 h-16 rounded-full mx-auto mb-3 border-2 border-purple-400"
                        />
                        <p className="text-white font-medium">{userRole}</p>
                      </div>
                      <div className="flex justify-center gap-4">
                        <button
                          onClick={() => {
                            resetAllTabs();
                            setShowAccountInfo(true);
                            setShowProfileDropdown(false); // close dropdown
                          }}
                          className="p-3 bg-gray-700/50 rounded-full hover:bg-gray-600/60 transition"
                          title="Profile"
                        >
                          <PersonOutlineIcon style={{ fontSize: 20, color: "#fff" }} />
                        </button>
                        <button
                          onClick={() => {
                            handleSignOut();
                            setShowProfileDropdown(false); // close dropdown
                          }}
                          className="p-3 bg-red-900/40 rounded-full hover:bg-red-600/60 transition"
                          title="Logout"
                        >
                          <ExitToApp style={{ fontSize: 20, color: "#fff" }} />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminHeaderSidebarAndTopbar;